import React, { useState } from 'react';

// Design System Components
import TextField from 'Components/DesignSystem/TextField/TextField';
import Button from 'Components/DesignSystem/Button/Button';
import Typography from 'Components/DesignSystem/Typography/Typography';

// Local Components
import ModalWrapper from './ModalWrapper';

// Types
import { IgnoreReason, IgnoreProductModalData } from '../types';

// Constants
import { IGNORE_REASONS } from '../constants';

interface IgnoreProductModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: IgnoreProductModalData) => void;
  productName?: string;
}

/**
 * IgnoreProductModal Component
 *
 * Modal for ignoring a product with reason selection.
 * - Text input for custom comment
 * - Suggested reasons as chip buttons
 * - Submit when reason is selected/entered
 */
const IgnoreProductModal = ({
  isOpen,
  onClose,
  onSubmit,
  productName,
}: IgnoreProductModalProps) => {
  // State
  const [selectedReason, setSelectedReason] = useState<IgnoreReason | null>(null);
  const [customComment, setCustomComment] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Reset state when modal closes
  const handleClose = () => {
    setSelectedReason(null);
    setCustomComment('');
    onClose();
  };

  // Handlers
  const handleReasonClick = (reason: IgnoreReason) => {
    if (selectedReason === reason) {
      setSelectedReason(null);
    } else {
      setSelectedReason(reason);
      setCustomComment(''); // Clear custom when selecting preset
    }
  };

  const handleCustomCommentChange = (value: string) => {
    setCustomComment(value);
    if (value) {
      setSelectedReason(null); // Clear preset when typing custom
    }
  };

  const handleSubmit = async () => {
    const reason = selectedReason || customComment;
    if (!reason) return;

    setIsSubmitting(true);
    try {
      await onSubmit({
        reason: selectedReason || customComment,
        customComment: selectedReason ? '' : customComment,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const hasValidReason = selectedReason || customComment.trim().length > 0;

  const renderFooter = () => (
    <div className="tw-flex tw-justify-end tw-gap-3">
      <Button variant="tertiary" onClick={handleClose} disabled={isSubmitting} data-testid="catalog-ignore-product-cancel-btn">
        Cancel
      </Button>
      <Button
        variant="primary"
        onClick={handleSubmit}
        disabled={!hasValidReason || isSubmitting}
      >
        {isSubmitting ? 'Ignoring...' : 'Ignore Product'}
      </Button>
    </div>
  );

  return (
    <ModalWrapper
      isOpen={isOpen}
      onClose={handleClose}
      title="Ignore Product"
      footer={renderFooter()}
      width="md"
    >
      <div className="tw-space-y-6">
        {/* Product Name Display */}
        {productName && (
          <div className="tw-p-3 tw-bg-surface-sunken tw-rounded tw-border tw-border-edge">
            <Typography variant="body-primary" className="tw-text-ink-secondary">
              You are about to ignore:
            </Typography>
            <Typography variant="title" className="tw-text-ink tw-mt-1">
              {productName}
            </Typography>
          </div>
        )}

        {/* Custom Comment Input */}
        <TextField
          label="Reason"
          value={customComment}
          onChange={handleCustomCommentChange}
          placeholder="Enter comment"
        />

        {/* Suggested Reasons */}
        <div>
          <Typography variant="sub-title-label" className="tw-text-ink-secondary tw-mb-3">
            Suggested Comments
          </Typography>
          <div className="tw-flex tw-flex-wrap tw-gap-2">
            {IGNORE_REASONS.map((reason) => (
              <button data-testid="modals-ignoreproductmodal-button-1"
                key={reason}
                onClick={() => handleReasonClick(reason)}
                className={`tw-px-3 tw-py-1.5 tw-rounded tw-border tw-text-sm tw-transition-colors ${
                  selectedReason === reason
                    ? 'tw-bg-accent-tint tw-border-accent-hover tw-text-accent'
                    : 'tw-bg-surface-sunken tw-border-edge tw-text-ink-secondary hover:tw-bg-surface-hover-strong'
                }`}
              >
                {reason}
              </button>
            ))}
          </div>
        </div>
      </div>
    </ModalWrapper>
  );
};

export default IgnoreProductModal;
